import type { FilterGroup, FilterValue } from '@app/src/pages/ResourceCollection/Filters/useFilters'
import type { AssessmentTemplate } from '@app/src/types/resourceExplorer'
import type { IntlFormatters } from 'react-intl'
import { formatLevelLabel } from '../utils/assessmentLevelTranslations'

export const ASSESSMENT_TEMPLATE_ID_PROP = 'assessments.assessmentTemplate.id'
export const TOTAL_LEVEL_PROP = 'assessments.totalLevel'
export const SECTION_LEVEL_PROP = 'assessments.section'
export const COUNTRY_INDEX_PROP = 'countryIndex'
export const INDUSTRY_INDEX_PROP = 'industryIndex'
export const COUNTRY_RISK_PROP = 'country'
export const INDUSTRY_RISK_PROP = 'industry'

const findSelectedTemplate = (
  filters: FilterGroup[],
  templates: AssessmentTemplate[],
): AssessmentTemplate | undefined => {
  const selectedId = Number(filters.find(f => f.name === ASSESSMENT_TEMPLATE_ID_PROP)?.filters?.[0]?.value)
  return templates.find(t => t.id === selectedId)
}

const getTranslatedLevel = (
  template: AssessmentTemplate,
  formatMessage: IntlFormatters['formatMessage'],
  value: FilterValue | undefined,
): string => {
  const getLabel = (val: string | number | boolean | undefined) => {
    const level = Number(val)
    return Number.isInteger(level) ? formatLevelLabel(template, level, formatMessage) : String(val)
  }

  return Array.isArray(value) ? value.map(getLabel).join(', ') : getLabel(value)
}

export const getAssessmentFilterLabel = (
  property: string,
  value: FilterValue | undefined,
  formatMessage: IntlFormatters['formatMessage'],
  filters: FilterGroup[],
  assessmentTemplates: AssessmentTemplate[],
): string | undefined => {
  const selectedTemplate = findSelectedTemplate(filters, assessmentTemplates)
  if (!selectedTemplate) return undefined

  if (property === TOTAL_LEVEL_PROP) {
    const levelLabel = getTranslatedLevel(selectedTemplate, formatMessage, value)
    return `${selectedTemplate.name}: ${levelLabel}`
  }

  return `${selectedTemplate.name}`
}

export const getAssessmentChipLabel = (
  filters: FilterGroup[],
  formatMessage: IntlFormatters['formatMessage'],
  assessmentTemplates: AssessmentTemplate[],
): string | undefined => {
  const totalLevel = filters.find(f => f.name === TOTAL_LEVEL_PROP)
  const template = filters.find(f => f.name === ASSESSMENT_TEMPLATE_ID_PROP)
  const chosen = totalLevel ?? template

  if (!chosen) return undefined

  return getAssessmentFilterLabel(chosen.name, chosen.filters[0].value, formatMessage, filters, assessmentTemplates)
}

export const getAssessmentChipLabels = (
  filters: FilterGroup[],
  formatMessage: IntlFormatters['formatMessage'],
  assessmentTemplates: AssessmentTemplate[],
): string[] => {
  const selectedTemplate = findSelectedTemplate(filters, assessmentTemplates)
  if (!selectedTemplate) return []

  const chips: string[] = []

  const totalLevelFilter = filters.find(f => f.name === TOTAL_LEVEL_PROP)
  if (totalLevelFilter) {
    const rawValue = totalLevelFilter.filters[0].value
    const normalizedValues = Array.isArray(rawValue)
      ? rawValue.map(v => v.toString()).sort((a, b) => Number(a) - Number(b))
      : rawValue !== undefined
        ? [rawValue.toString()]
        : []
    const levelLabel = getTranslatedLevel(selectedTemplate, formatMessage, normalizedValues)
    chips.push(`${selectedTemplate.name}: ${levelLabel}`)
  }

  filters
    .filter(f => f.name.startsWith(SECTION_LEVEL_PROP))
    .forEach(f => {
      const sectionNum = Number(f.name.match(/section(\d+)Level/)?.[1])
      const section = selectedTemplate.sections?.find(s => s.assessmentSectionNumber === sectionNum)
      const sectionLabel = getTranslatedLevel(selectedTemplate, formatMessage, f.filters[0].value)
      if (section?.name && sectionLabel) {
        chips.push(`${section.name}: ${sectionLabel}`)
      }
    })

  return chips
}
